import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import { PhoneField, SelectField, TextArea, TextField } from '../components/FormField';
import RelationField from '../components/RelationField';
import { formatCountry, formatDate, formatPhone } from '../utils/formatters';
import useSharePointList from '../hooks/useSharePointList';
import useAuth from '../auth/useAuth';
import {
  APPAREL_SIZES,
  SHIRT_FITS,
  VEST_COLORS,
  createRequest,
} from '../services/requests';
import { listTeam, updateTeamMember } from '../services/team';
import { listPlants } from '../services/plants';
import {
  createTeamMemberResource,
  deleteTeamMemberResource,
  listTeamMemberResources,
} from '../services/teamMemberResources';
import { notifyRequestSubmitted } from '../services/email';
import { displayFullName } from '../utils/teamDisplay';
import { ResourceForm, ResourcesGrid } from '../components/ResourceLibrary';
import {
  listSalesTrackerEvents,
  deleteSalesTrackerEvent,
} from '../services/salesTracker';
import {
  listTimesheets,
  deleteTimesheet,
  BILLING_TRACKER_FIRST_WEEK,
} from '../services/timesheets';
import { listInvoices, deleteInvoice } from '../services/invoices';

/**
 * "My Account" landing page. Any signed-in team member sees:
 *   1. A "Profile" card at the top — name, mailing address (with an
 *      "Update Address" shortcut), phone number (with an "Update Phone"
 *      shortcut), and their plant assignments.
 *   2. "Start a Request" — cards opening typed request forms for
 *      physical items (Vest, Shirt, Business Cards, Badge Card). The
 *      Address Change and Phone Change forms are reachable from the
 *      profile card above, not here — one obvious entry point per
 *      action keeps this page uncluttered.
 *
 * No admin/inbox view lives here — Directors review and action requests
 * somewhere else (SharePoint list view for now). We deliberately kept
 * this page purely self-service so it's safe to share the URL with any
 * team member regardless of role.
 *
 * Forms live as sub-components in this file to match the Sales.jsx
 * convention — only one screen reads them, so a shared file keeps the
 * related forms close together.
 */
export default function MyAccount() {
  const { name, email, isDirector } = useAuth();

  const team = useSharePointList(listTeam);
  const plants = useSharePointList(listPlants);
  // Team Member Resources — every team member can see the tiles; only
  // Directors see the Upload / Remove controls. Mirrors the Sales
  // Resources setup so both surfaces behave predictably.
  const resources = useSharePointList(listTeamMemberResources);

  // Which request-type form is open in the modal (null = no modal).
  const [activeType, setActiveType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Team Member Resources upload-modal state. Upload failures surface
  // inside the modal (not as a page-level error) so Directors can retry
  // or read the actual SharePoint message without losing the form.
  const [addingResource, setAddingResource] = useState(false);
  const [resourceSaving, setResourceSaving] = useState(false);
  const [resourceError, setResourceError] = useState('');

  const openType = (type) => {
    setSubmitError('');
    setActiveType(type);
  };

  const closeModal = () => {
    if (saving) return;
    setSubmitError('');
    setActiveType(null);
  };

  // ---- Derive the viewer's profile from the Team list -----------------
  // SharePoint usernames are stamped as-entered; lower-case both sides so
  // a mismatch in casing (Jillian.Lamb@... vs jillian.lamb@...) doesn't
  // silently hide someone's profile.
  //
  // This block has to live *above* the onSubmit useCallback below — it
  // reads `profile` inside its dependency array, and referencing a `const`
  // before its declaration is a JS temporal-dead-zone crash that blanks
  // the whole page. Keep this ordering.
  const normalisedEmail = (email || '').toLowerCase();
  const profile = useMemo(() => {
    if (!team.data) return null;
    return (
      team.data.find(
        (m) => (m.Email || '').toLowerCase() === normalisedEmail,
      ) || null
    );
  }, [team.data, normalisedEmail]);

  const onSubmit = useCallback(
    async (draft) => {
      setSaving(true);
      setSubmitError('');
      try {
        // Address + Phone changes auto-apply to the Proxemics Team
        // record so the roster stays in sync without a Director step.
        // We still file a Request row (marked Fulfilled) for audit — so
        // there's a history of who changed what and when. Other request
        // types (Vest, Shirt, etc.) stay in the classic "Submitted →
        // Director reviews" flow.
        const autoApply =
          profile &&
          (activeType === 'Address Change' ||
            activeType === 'Phone Change' ||
            activeType === 'Emergency Contact Change' ||
            activeType === 'Shirt Size Change' ||
            activeType === 'Vest Size Change');

        if (autoApply) {
          // Merge the new values into the full decoded profile so encode()
          // doesn't wipe plantCodes/liaisonManagers — passing a partial
          // object would re-serialize those arrays as empty.
          const patch = { ...profile };
          if (activeType === 'Address Change') {
            patch.MailingStreet = draft.NewStreet || '';
            patch.MailingCity = draft.NewCity || '';
            patch.MailingState = draft.NewState || '';
            patch.MailingZip = draft.NewZip || '';
            patch.MailingCountry = draft.NewCountry || '';
          } else if (activeType === 'Phone Change') {
            patch.Phone = draft.NewPhone || '';
          } else if (activeType === 'Emergency Contact Change') {
            patch.EmergencyContactName = draft.NewEmergencyContactName || '';
            patch.EmergencyContactPhone = draft.NewEmergencyContactPhone || '';
            patch.EmergencyContactRelation =
              draft.NewEmergencyContactRelation || '';
          } else if (activeType === 'Shirt Size Change') {
            patch.ShirtSize = draft.NewShirtSize || '';
            patch.ShirtFit = draft.NewShirtFit || '';
          } else if (activeType === 'Vest Size Change') {
            patch.VestSize = draft.NewVestSize || '';
            patch.VestColor = draft.NewVestColor || '';
            // Clear the fill-in when the user switched away from Other,
            // so a stale value doesn't linger on the team record.
            patch.VestColorOther =
              draft.NewVestColor === 'Other'
                ? draft.NewVestColorOther || ''
                : '';
          }
          await updateTeamMember(profile.id, patch);
        }

        const requestPayload = {
          ...draft,
          RequestType: activeType,
          RequestedBy: name || email || '',
          RequestedByEmail: email || '',
        };
        if (autoApply) {
          requestPayload.Status = 'Fulfilled';
          requestPayload.ReviewedBy = 'System (Auto-Applied)';
          requestPayload.ReviewedAt = new Date().toISOString();
          requestPayload.StatusNote =
            'Applied automatically to the Proxemics Team record.';
        }
        await createRequest(requestPayload);

        setActiveType(null);

        // Refetch the team list so the profile card re-renders with the
        // new address/phone values. Plant assignments also come from
        // this fetch, so any Director-side roster changes show up too.
        if (autoApply) {
          await team.refresh();
        }

        // Notify Directors (To) + the submitter's Regional Manager (CC)
        // that a new request was submitted. Non-fatal: the SharePoint
        // row is already saved, so a failed email shouldn't surface as
        // a submit error. We log instead so a missing Mail.Send consent
        // shows up in the console during rollout.
        try {
          await notifyRequestSubmitted({
            request: requestPayload,
            submitter: profile,
            submitterName: name || '',
            submitterEmail: email || '',
            team: team.data || [],
            appliedAlready: !!autoApply,
          });
        } catch (emailErr) {
          // eslint-disable-next-line no-console
          console.warn(
            '[MyAccount] Request saved, but email notification failed:',
            emailErr,
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[MyAccount] Submit failed:', err);
        setSubmitError(
          err?.message ||
            'Submit failed for an unknown reason. Check the browser console for details.',
        );
      } finally {
        setSaving(false);
      }
    },
    [activeType, email, name, profile, team],
  );

  // ---- Team Member Resources upload handlers ---------------------------
  // Kept parallel to the Sales Resources upload on Sales.jsx so Directors
  // see the same "+ Upload Resource" → modal → Upload / Cancel pattern
  // from both surfaces. Only Directors reach the button (guarded below),
  // but we validate on server-side via SharePoint permissions too.
  const onAddResource = () => {
    setResourceError('');
    setAddingResource(true);
  };

  const onSaveResource = useCallback(
    async (draft) => {
      setResourceSaving(true);
      setResourceError('');
      try {
        await createTeamMemberResource({
          file: draft.file,
          Title: draft.Title,
          Description: draft.Description,
          Category: draft.Category,
          PostedBy: name || email || '',
          PostedAt: new Date().toISOString(),
        });
        setAddingResource(false);
        await resources.refresh();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[MyAccount] Resource upload failed:', err);
        setResourceError(
          err?.message ||
            'Upload failed for an unknown reason. Check the browser console for details.',
        );
      } finally {
        setResourceSaving(false);
      }
    },
    [resources, name, email],
  );

  const onRemoveResource = async (row) => {
    // Plain browser confirm — same low-stakes delete pattern used by
    // Sales Resources and Parts. Deliberate click by a Director, no
    // need for a dedicated confirm modal.
    if (!window.confirm(`Remove “${row.Title}” from Team Member Resources?`)) {
      return;
    }
    await deleteTeamMemberResource(row.id);
    await resources.refresh();
  };

  // Map plant codes → display names so the Profile card can show
  // "Sterling Heights Assembly Plant (SHAP)" instead of just "SHAP".
  const plantsByCode = useMemo(() => {
    const map = {};
    for (const p of plants.data || []) {
      if (p.PlantCode) map[p.PlantCode] = p;
    }
    return map;
  }, [plants.data]);

  // Prefill the Address Change form with the user's current address —
  // most updates are partial (ZIP change, street number, etc.) so it's
  // friendlier than a blank form. Always null-safe.
  const addressInitial = profile
    ? {
        NewStreet: profile.MailingStreet || '',
        NewCity: profile.MailingCity || '',
        NewState: profile.MailingState || '',
        NewZip: profile.MailingZip || '',
        NewCountry: profile.MailingCountry || 'United States',
      }
    : null;

  // Same idea for the Phone Change form — prefill with the current
  // number so the user just edits the digits.
  const phoneInitial = profile ? { NewPhone: profile.Phone || '' } : null;

  // Same idea for the Emergency Contact Change form — prefill with the
  // existing contact so the user can correct just the field that changed
  // (a new number, a different relationship) without re-typing the rest.
  const emergencyInitial = profile
    ? {
        NewEmergencyContactName: profile.EmergencyContactName || '',
        NewEmergencyContactPhone: profile.EmergencyContactPhone || '',
        NewEmergencyContactRelation: profile.EmergencyContactRelation || '',
      }
    : null;

  // Shirt Size Change form prefill — start with the size + fit on file
  // so the user just picks a different one from the dropdowns.
  const shirtSizeInitial = profile
    ? {
        NewShirtSize: profile.ShirtSize || '',
        NewShirtFit: profile.ShirtFit || '',
      }
    : null;

  // Vest Size Change form prefill — same pattern as shirt size. Brings
  // forward the current size + color so the member is only picking the
  // things they're actually changing.
  const vestSizeInitial = profile
    ? {
        NewVestSize: profile.VestSize || '',
        NewVestColor: profile.VestColor || '',
        NewVestColorOther: profile.VestColorOther || '',
      }
    : null;

  // Business-card contact defaults — the printed name / phone / email
  // come straight from the team record so the form doesn't start blank
  // and the user just eyeballs + confirms. Falls back to auth claims for
  // the rare case where their team record hasn't been loaded yet.
  const cardContact = useMemo(() => {
    // Use the display-preferred name (nickname when set) so business cards
    // and badge cards print the name the team member actually goes by.
    const fullName = profile ? displayFullName(profile) : '';
    return {
      name: fullName || name || '',
      // `Role` is the job-title column on the Team list (e.g. "Director of
      // Business Development"), same value the profile card shows under
      // the person's name. Printing from the same field keeps cards and
      // profile in lockstep.
      title: profile?.Role || '',
      email: profile?.Email || email || '',
      phone: profile?.Phone || '',
    };
  }, [profile, name, email]);

  // ---- Derive Key Contacts for Support -------------------------------
  // "Day-to-Day Operations & Reports" pulls live from the team roster:
  // anyone with PortalRole RegionalManager or LiaisonManager whose
  // plantCodes overlap the viewer's. That way plant staffing changes
  // show up here automatically (no hard-coded names for the variable
  // part of the list).
  //
  // The remaining rows (Email & Proxemics Portal, HR & Payroll, Billing
  // & Invoices, Sales Inquiries) are company-wide fixed roles. We still
  // look them up in the roster by First + Last so each name can link to
  // their /team/:id page. If a lookup fails (they're not on the roster
  // yet, or the name changed), the row falls back to plain text.
  const keyContacts = useMemo(() => {
    const roster = team.data || [];
    const myPlants = Array.isArray(profile?.plantCodes)
      ? profile.plantCodes
      : [];

    const findByName = (first, last) => {
      const f = first.toLowerCase();
      const l = last.toLowerCase();
      return (
        roster.find(
          (m) =>
            (m.FirstName || '').toLowerCase() === f &&
            (m.LastName || '').toLowerCase() === l,
        ) || null
      );
    };

    const byLastName = (a, b) =>
      (a.LastName || '').localeCompare(b.LastName || '') ||
      (a.FirstName || '').localeCompare(b.FirstName || '');

    // Only active / on-leave staff — exited team members shouldn't be
    // listed as someone's day-to-day contact.
    const ACTIVE = new Set(['Active', 'On Leave']);
    const dayToDayMembers =
      myPlants.length === 0
        ? []
        : roster
            .filter(
              (m) =>
                ACTIVE.has(m.EmploymentStatus) &&
                (m.PortalRole === 'RegionalManager' ||
                  m.PortalRole === 'LiaisonManager') &&
                Array.isArray(m.plantCodes) &&
                m.plantCodes.some((c) => myPlants.includes(c)),
            )
            .sort(byLastName);

    // Build a row's name list as {name, id} pairs — render logic stays
    // dead simple: link if id, plain text if not.
    const toEntry = (member, fallbackName) => {
      if (member) {
        return { name: displayFullName(member) || fallbackName, id: member.id };
      }
      return { name: fallbackName, id: null };
    };

    // The first row depends on the viewer's role:
    //   Regional Manager → "Direct Reports": the Customer Liaison Managers who
    //                      report to them (matched on the RM name stored on
    //                      each LM's record); the RM themselves is excluded.
    //   Liaison Manager  → "Manager": their own Regional Manager.
    //   Everyone else    → the original plant-overlap "Day-to-Day" list.
    const viewerRole = profile?.PortalRole || '';
    const myLegalName = profile
      ? [profile.FirstName, profile.LastName].filter(Boolean).join(' ').trim()
      : '';
    const myLegalLower = myLegalName.toLowerCase();
    const nameMatches = (m, target) => {
      const t = target.toLowerCase();
      return (
        (displayFullName(m) || '').toLowerCase() === t ||
        [m.FirstName, m.LastName].filter(Boolean).join(' ').toLowerCase() === t
      );
    };

    let leadingRows;
    if (viewerRole === 'RegionalManager') {
      const myReports = roster
        .filter(
          (m) =>
            ACTIVE.has(m.EmploymentStatus) &&
            m.PortalRole === 'LiaisonManager' &&
            (m.RegionalManager || '').trim().toLowerCase() === myLegalLower,
        )
        .sort(byLastName);
      leadingRows = [
        {
          label: 'Direct Reports',
          entries: myReports.map((m) => ({ name: displayFullName(m), id: m.id })),
          emptyMessage: 'No Customer Liaison Managers report to you yet.',
        },
      ];
    } else if (viewerRole === 'LiaisonManager') {
      const rmName = (profile?.RegionalManager || '').trim();
      const rmMember = rmName
        ? roster.find((m) => nameMatches(m, rmName)) || null
        : null;
      // Liaisons who report to this LM — i.e. ones with this LM's name in
      // their liaisonManagers list.
      const myLiaisons = roster
        .filter(
          (m) =>
            ACTIVE.has(m.EmploymentStatus) &&
            m.PortalRole === 'Liaison' &&
            Array.isArray(m.liaisonManagers) &&
            m.liaisonManagers.some(
              (n) => (n || '').trim().toLowerCase() === myLegalLower,
            ),
        )
        .sort(byLastName);
      leadingRows = [
        {
          label: 'Manager',
          entries: rmName ? [toEntry(rmMember, rmName)] : [],
          emptyMessage: 'No Regional Manager assigned to you yet.',
        },
        {
          label: 'Direct Reports',
          entries: myLiaisons.map((m) => ({
            name: displayFullName(m),
            id: m.id,
          })),
          emptyMessage: 'No liaisons report to you yet.',
        },
      ];
    } else {
      leadingRows = [
        {
          label: 'Day-to-Day Operations & Reports',
          entries: dayToDayMembers.map((m) => ({
            name: displayFullName(m),
            id: m.id,
          })),
          emptyMessage:
            myPlants.length === 0
              ? 'Your plant assignments will appear once a Director adds you to a plant.'
              : 'No Regional Manager or Customer Liaison Manager assigned to your plant yet.',
        },
      ];
    }

    return [
      ...leadingRows,
      {
        label: 'Email & Proxemics Portal',
        entries: [toEntry(findByName('Jillian', 'Lamb'), 'Jillian Lamb')],
      },
      {
        label: 'HR & Payroll',
        entries: [
          toEntry(findByName('Deanna', 'Gheldof'), 'Deanna Gheldof'),
          toEntry(findByName('Judy', 'Gheldof'), 'Judy Gheldof'),
        ],
      },
      {
        label: 'Billing & Invoices',
        entries: [toEntry(findByName('Joyelle', 'Maxwell'), 'Joyelle Maxwell')],
      },
      {
        label: 'Sales Inquiries',
        entries: [toEntry(findByName('Chris', 'Maxwell'), 'Chris Maxwell')],
      },
    ];
  }, [team.data, profile]);

  // Mailing address in a shape the shipping forms (Vest, Shirt, Business
  // Cards, Badge Card) can render directly. We deliberately return `null`
  // if the user has literally nothing on file so the form can show a
  // "please add an address first" gate instead of an empty address block
  // with an un-checkable confirmation box.
  const mailingAddress = useMemo(() => {
    if (!profile) return null;
    const hasAny =
      profile.MailingStreet ||
      profile.MailingCity ||
      profile.MailingState ||
      profile.MailingZip ||
      profile.MailingCountry;
    if (!hasAny) return null;
    return {
      street: profile.MailingStreet || '',
      city: profile.MailingCity || '',
      state: profile.MailingState || '',
      zip: profile.MailingZip || '',
      country: profile.MailingCountry || '',
    };
  }, [profile]);

  return (
    <>
      <div className="page-header">
        <h1>My Account</h1>
      </div>

      {/* ---------- Profile ---------- */}
      <section style={{ marginBottom: 32 }}>
        {team.loading && <Loading />}
        {team.error && (
          <div
            className="card"
            style={{ borderColor: 'var(--brand-danger)' }}
          >
            <div className="card-title">Couldn't Load Your Profile</div>
            <p>{team.error.message}</p>
            <button
              className="btn btn-outline btn-sm"
              onClick={team.refresh}
            >
              Retry
            </button>
          </div>
        )}
        {!team.loading && !team.error && (
          <ProfileCard
            authName={name}
            authEmail={email}
            profile={profile}
            plantsByCode={plantsByCode}
            onUpdateAddress={() => openType('Address Change')}
            onUpdatePhone={() => openType('Phone Change')}
            onUpdateEmergency={() => openType('Emergency Contact Change')}
            onUpdateShirtSize={() => openType('Shirt Size Change')}
            onUpdateVest={() => openType('Vest Size Change')}
          />
        )}
      </section>

      {/* ---------- Team Member Resources ---------- */}
      {/* Internal training library — onboarding decks, SOPs, quick-
          reference PDFs, how-to videos. Directors upload + remove;
          every team member can download. Backed by the
          TeamMemberResources SharePoint document library (separate
          from SalesResources so customer-facing collateral stays
          isolated from internal materials). */}
      <section style={{ marginBottom: 32 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ marginTop: 24, marginBottom: 0 }}>Team Member Resources</h2>
          {isDirector && (
            <button className="btn btn-primary" onClick={onAddResource}>
              + Upload Resource
            </button>
          )}
        </div>

        {resources.loading && <Loading />}
        {resources.error && (
          <div
            className="card"
            style={{ borderColor: 'var(--brand-danger)' }}
          >
            <div className="card-title">Couldn't Load Resources</div>
            <p>{resources.error.message}</p>
            <button
              className="btn btn-outline btn-sm"
              onClick={resources.refresh}
            >
              Retry
            </button>
          </div>
        )}

        {!resources.loading && !resources.error && (
          <ResourcesGrid
            resources={resources.data || []}
            canManage={isDirector}
            onRemove={onRemoveResource}
            emptyMessage={
              isDirector
                ? 'No team member resources yet. Click “+ Upload Resource” to add an SOP, onboarding deck, or quick-reference PDF.'
                : 'No team member resources yet. Check back soon.'
            }
          />
        )}

        {/* ---- Key Contacts for Support ---- */}
        {/* Sits under Team Member Resources as a companion subsection so
            the "where do I find help?" answers live next to the "where
            do I find the documents?" answers. Day-to-day contacts come
            from the roster; everything else is a company-wide role. */}
        <KeyContactsCard contacts={keyContacts} />
      </section>

      {/* ---------- Pick a request type ---------- */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginTop: 24 }}>Need Something?</h2>
        <RequestTypeGrid onPick={openType} />
      </section>

      {/* ---------- Pre-Launch Cleanup (Director-only) ----------
          Temporary panel for clearing the Coverage Activity events
          and timesheets the current user posted during setup, so
          the portal launches with a clean slate for everyone else.
          Gated to Directors so non-admin team members never see
          the destructive controls. Safe to remove this whole
          section after go-live. */}
      {isDirector && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ marginTop: 24, marginBottom: 12 }}>
            Pre-Launch Cleanup
          </h2>
          <PreLaunchCleanupCard authName={name} authEmail={email} />
        </section>
      )}

      {/* ---------- Modal (one modal, contents swap by type) ---------- */}
      {/* Title wording differs by flow: the auto-apply forms
          (Address / Phone / Emergency Contact / Shirt Size) are
          "Updates" — they save straight to the team record — while the
          physical-goods forms stay as "Requests" because a Director
          still needs to review + fulfill them. */}
      <Modal
        open={!!activeType}
        onClose={closeModal}
        title={
          activeType
            ? activeType.endsWith(' Change')
              ? `${activeType.replace(/ Change$/, '')} Update`
              : `New ${activeType} Request`
            : ''
        }
      >
        {activeType && (
          <RequestForm
            type={activeType}
            addressInitial={addressInitial}
            phoneInitial={phoneInitial}
            emergencyInitial={emergencyInitial}
            shirtSizeInitial={shirtSizeInitial}
            vestSizeInitial={vestSizeInitial}
            mailingAddress={mailingAddress}
            cardContact={cardContact}
            saving={saving}
            error={submitError}
            onSave={onSubmit}
            onCancel={closeModal}
          />
        )}
      </Modal>

      {/* ---------- Upload Team Member Resource (Director-only) --------- */}
      <Modal
        open={addingResource}
        onClose={() => {
          // Block dismissal mid-upload so the Graph call doesn't get
          // orphaned from the UI state that tracks it.
          if (resourceSaving) return;
          setResourceError('');
          setAddingResource(false);
        }}
        title="Upload Team Member Resource"
      >
        {addingResource && (
          <ResourceForm
            saving={resourceSaving}
            error={resourceError}
            categoryPlaceholder="e.g. SOP, Onboarding, Quick Reference"
            onSave={onSaveResource}
            onCancel={() => {
              setResourceError('');
              setAddingResource(false);
            }}
          />
        )}
      </Modal>
    </>
  );
}

// =====================================================================
// Request-type picker — cards in a grid, each opens a form modal.
// Address Change / Phone Change aren't listed here — they're launched
// from the "Update Address" / "Update Phone" buttons inside the profile
// card above so there's one obvious entry point for each.
// =====================================================================

const TYPE_CARDS = [
  {
    type: 'Vest',
    label: 'Request Safety Vest',
    description: 'Proxemics safety vests in specified color.',
  },
  {
    type: 'Shirt',
    label: 'Request Shirt',
    description: 'Proxemics branded polo shirt.',
  },
  {
    type: 'Business Cards',
    label: 'Request Business Cards',
    description: 'Standard business cards with QR code.',
  },
  {
    type: 'Badge Card',
    label: 'Request Badge Card',
    description: 'QR badge sized to fit safety vest pocket.',
  },
];

// =====================================================================
// Profile card — name, mailing address + phone (each with update CTA),
// and the user's plant assignments.
// =====================================================================

function ProfileCard({
  authName,
  authEmail,
  profile,
  plantsByCode,
  onUpdateAddress,
  onUpdatePhone,
  onUpdateEmergency,
  onUpdateShirtSize,
  onUpdateVest,
}) {
  // Prefer the Team list record (it's canonical), honoring any nickname the
  // member has set. Falls back to the signed-in user's name/email claim if
  // there's no matching row yet.
  const displayName =
    (profile && displayFullName(profile)) ||
    profile?.Title ||
    authName ||
    '—';
  const displayEmail = profile?.Email || authEmail || '';

  // Build a single-line address out of the structured columns. Kept as
  // a list of comma-joinable parts so an empty city/zip doesn't leave
  // an awkward ", ," in the output.
  const addressLine1 = profile?.MailingStreet || '';
  const addressLine2 = [profile?.MailingCity, profile?.MailingState]
    .filter(Boolean)
    .join(', ');
  // Zip rides with Country on the last line so the block reads like a
  // conventional international mailing label (street / city, state /
  // zip, country).
  const addressLine3 = [profile?.MailingZip, formatCountry(profile?.MailingCountry)]
    .filter(Boolean)
    .join(', ');
  const hasAddress = addressLine1 || addressLine2 || addressLine3;

  const plantCodes = Array.isArray(profile?.plantCodes)
    ? profile.plantCodes
    : [];

  return (
    <div className="card" style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            className="card-title"
            style={{
              marginBottom: 2,
              fontSize: 20,
              color: 'var(--brand-ink)',
            }}
          >
            {displayName}
          </div>
          {/* Title/role goes immediately under the name so it reads like
              a business-card signature block: Name → Title → Email. Uses
              the orange brand color so it visually ties the profile card
              back to the rest of the portal's accent styling. */}
          {profile?.Role && (
            <div
              style={{
                color: 'var(--brand-primary)',
                fontSize: 14,
                fontWeight: 400,
              }}
            >
              {profile.Role}
            </div>
          )}
          {displayEmail && (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              {displayEmail}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          // 5 tiles — Mailing Address, Phone, Emergency Contact, Shirt
          // Size, Plant Assignment — sized to fit on one row at typical
          // desktop widths. Minimum of 180px keeps each tile readable;
          // the grid still wraps gracefully on narrow viewports (phones,
          // split windows) so nothing gets squeezed off-screen.
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 20,
          marginTop: 20,
        }}
      >
        {/* ---- Mailing address ---- */}
        <div>
          <ProfileFieldLabel>Mailing Address</ProfileFieldLabel>
          {hasAddress ? (
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              {addressLine1 && <div>{addressLine1}</div>}
              {addressLine2 && <div>{addressLine2}</div>}
              {addressLine3 && <div>{addressLine3}</div>}
            </div>
          ) : (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              No address on file.
            </div>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: 10 }}
            onClick={onUpdateAddress}
          >
            Update Address
          </button>
        </div>

        {/* ---- Phone number ---- */}
        <div>
          <ProfileFieldLabel>Phone Number</ProfileFieldLabel>
          {profile?.Phone ? (
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              {formatPhone(profile.Phone)}
            </div>
          ) : (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              No phone number on file.
            </div>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: 10 }}
            onClick={onUpdatePhone}
          >
            Update Phone
          </button>
        </div>

        {/* ---- Emergency contact ---- */}
        {/* Pulls Name / Phone / Relation straight off the team record so
            the card reads the same information the Director sees on the
            Team detail page. Update button launches the same auto-apply
            flow used by Update Address / Update Phone — the submitted
            values write back to the team record immediately. */}
        <div>
          <ProfileFieldLabel>Emergency Contact</ProfileFieldLabel>
          {profile?.EmergencyContactName ||
          profile?.EmergencyContactPhone ||
          profile?.EmergencyContactRelation ? (
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              {profile.EmergencyContactName && (
                <div>{profile.EmergencyContactName}</div>
              )}
              {profile.EmergencyContactPhone && (
                <div>{formatPhone(profile.EmergencyContactPhone)}</div>
              )}
              {profile.EmergencyContactRelation && (
                <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
                  {profile.EmergencyContactRelation}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              No emergency contact on file.
            </div>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: 10 }}
            onClick={onUpdateEmergency}
          >
            Update Contact
          </button>
        </div>

        {/* ---- Shirt size ---- */}
        {/* Sits next to Emergency Contact so the four compact "personal
            details" tiles — Address, Phone, Emergency Contact, Shirt
            Size — line up as one row. The Update button launches the
            same auto-apply flow as Update Address / Update Phone: the
            new size writes back to the team record immediately so the
            next apparel request uses it automatically. */}
        <div>
          <ProfileFieldLabel>Polo Shirt</ProfileFieldLabel>
          {profile?.ShirtSize || profile?.ShirtFit ? (
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              {profile.ShirtSize && <div>{profile.ShirtSize}</div>}
              {profile.ShirtFit && (
                <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
                  {profile.ShirtFit}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              No polo shirt size on file.
            </div>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: 10 }}
            onClick={onUpdateShirtSize}
          >
            Update Shirt
          </button>
        </div>

        {/* ---- Safety Vest ---- */}
        {/* Mirrors the Polo Shirt tile — size on top line, color on the
            muted second line. Update Vest runs the same auto-apply flow
            as Update Polo Shirt: saves back to the team record and logs
            a Fulfilled request row for audit. When VestColor is "Other",
            we render the fill-in stored in VestColorOther so the member
            sees the actual color they're assigned. */}
        <div>
          <ProfileFieldLabel>Safety Vest</ProfileFieldLabel>
          {profile?.VestSize || profile?.VestColor ? (
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              {profile.VestSize && <div>{profile.VestSize}</div>}
              {(profile.VestColor || profile.VestColorOther) && (
                <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
                  {profile.VestColor === 'Other'
                    ? profile.VestColorOther || 'Other'
                    : profile.VestColor}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              No safety vest on file.
            </div>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: 10 }}
            onClick={onUpdateVest}
          >
            Update Vest
          </button>
        </div>

        {/* ---- Plant assignments ----
            Span the full grid width so the chips fill the row and wrap into
            a grid instead of stacking in one narrow column — important for
            Regional Managers, who can have many plants. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <ProfileFieldLabel>Plant Assignment</ProfileFieldLabel>
          {plantCodes.length === 0 ? (
            <div style={{ color: 'var(--brand-muted)', fontSize: 13 }}>
              No plants on file.
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
              }}
            >
              {plantCodes.map((code) => {
                const plant = plantsByCode[code];
                const label = plant?.Title
                  ? `${plant.Title} (${code})`
                  : code;
                return (
                  <span
                    key={code}
                    style={{
                      background: 'rgba(232, 119, 34, 0.08)',
                      color: 'var(--brand-primary)',
                      fontWeight: 600,
                      fontSize: 13,
                      padding: '4px 10px',
                      borderRadius: 999,
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* "Last updated" footer (per Jillian, Oct 2026). Gray text pinned
          to the lower-right corner of the card, one entry per contact
          group that has ever been changed through the portal. Stamps are
          written by services/team.js on every save that changes the
          group, whether the member used an Update button here or a
          Director edited the Team form. Records that have never been
          changed since the stamps were introduced show nothing. */}
      {profile && <ContactUpdatedFooter contactUpdated={profile.contactUpdated} />}

      {!profile && (
        <p
          style={{
            marginTop: 16,
            marginBottom: 0,
            color: 'var(--brand-muted)',
            fontSize: 13,
          }}
        >
          We couldn't find a team member record matching your email. Address
          and plant info will appear here once a Director adds you to the
          Proxemics Team list.
        </p>
      )}
    </div>
  );
}

// Which contact groups get a "last updated" line, in display order, and
// the label each one shows. Keys match services/team.js
// CONTACT_UPDATE_GROUPS.
const CONTACT_UPDATED_LABELS = [
  ['address', 'Address'],
  ['phone', 'Phone'],
  ['emergencyContact', 'Emergency contact'],
];

function ContactUpdatedFooter({ contactUpdated }) {
  const lines = CONTACT_UPDATED_LABELS.map(([key, label]) => {
    const when = formatDate(contactUpdated?.[key]);
    return when ? `${label} updated ${when}` : null;
  }).filter(Boolean);
  if (lines.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: '2px 14px',
        marginTop: 16,
        color: 'var(--brand-muted)',
        fontSize: 12,
        textAlign: 'right',
      }}
    >
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
}

// =====================================================================
// Key Contacts for Support — subsection under Team Member Resources.
// One row per support area: label on the left, clickable names on the
// right. Names link to the team member's /team/:id profile when we can
// resolve them on the roster; otherwise they fall back to plain text so
// the card still reads correctly during onboarding / edge cases.
// =====================================================================
function KeyContactsCard({ contacts }) {
  return (
    <div
      className="card"
      style={{ padding: 20, marginTop: 16 }}
    >
      <div className="card-title" style={{ marginBottom: 12 }}>
        Key Contacts for Support
      </div>
      <div
        style={{
          display: 'grid',
          // Label column auto-sizes to its content, names take the rest.
          // `minmax(0, 1fr)` keeps long name lists from pushing labels
          // out of their column when the card is narrow.
          gridTemplateColumns: 'max-content minmax(0, 1fr)',
          rowGap: 10,
          columnGap: 16,
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        {contacts.map((row) => (
          <KeyContactsRow key={row.label} row={row} />
        ))}
      </div>
    </div>
  );
}

function KeyContactsRow({ row }) {
  const hasAny = row.entries && row.entries.length > 0;
  return (
    <>
      <div style={{ color: 'var(--brand-muted)' }}>
        {row.label}:
      </div>
      <div>
        {hasAny ? (
          // Names are comma-separated inline so a row with two people
          // (HR, or a plant with multiple LMs) reads as one natural
          // sentence instead of a bulleted list.
          row.entries.map((entry, i) => (
            <span key={`${entry.name}-${i}`}>
              {i > 0 && ', '}
              {entry.id ? (
                <Link
                  to={`/team/${encodeURIComponent(entry.id)}`}
                  style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                >
                  {entry.name}
                </Link>
              ) : (
                <span>{entry.name}</span>
              )}
            </span>
          ))
        ) : (
          <span style={{ color: 'var(--brand-muted)' }}>
            {row.emptyMessage || '—'}
          </span>
        )}
      </div>
    </>
  );
}

function ProfileFieldLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: 'var(--brand-muted)',
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function RequestTypeGrid({ onPick }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 16,
        marginTop: 12,
      }}
    >
      {TYPE_CARDS.map((c) => (
        <button
          key={c.type}
          type="button"
          className="card"
          onClick={() => onPick(c.type)}
          style={{
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            border: '1px solid var(--brand-border)',
            background: '#fff',
            padding: 16,
          }}
        >
          <div className="card-title" style={{ marginBottom: 0 }}>
            {c.label}
          </div>
          <p style={{ margin: 0, color: 'var(--brand-muted)', fontSize: 13 }}>
            {c.description}
          </p>
          <span
            style={{
              marginTop: 'auto',
              color: 'var(--brand-primary)',
              fontWeight: 600,
              fontSize: 13,
              paddingTop: 12,
            }}
          >
            Start →
          </span>
        </button>
      ))}
    </div>
  );
}

// =====================================================================
// Forms — one component per type, rendered inside the same modal.
// Each owns its own state + validation; the outer page only handles
// submission wiring.
// =====================================================================

function RequestForm({
  type,
  addressInitial,
  phoneInitial,
  emergencyInitial,
  shirtSizeInitial,
  vestSizeInitial,
  mailingAddress,
  cardContact,
  saving,
  error,
  onSave,
  onCancel,
}) {
  switch (type) {
    case 'Vest':
      return (
        <VestOrShirtForm
          kind="Vest"
          mailingAddress={mailingAddress}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Shirt':
      return (
        <VestOrShirtForm
          kind="Shirt"
          mailingAddress={mailingAddress}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Business Cards':
      return (
        <BusinessCardsForm
          mailingAddress={mailingAddress}
          cardContact={cardContact}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Badge Card':
      return (
        <BadgeCardForm
          mailingAddress={mailingAddress}
          cardContact={cardContact}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Address Change':
      return (
        <AddressChangeForm
          initial={addressInitial}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Phone Change':
      return (
        <PhoneChangeForm
          initial={phoneInitial}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Emergency Contact Change':
      return (
        <EmergencyContactChangeForm
          initial={emergencyInitial}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Shirt Size Change':
      return (
        <ShirtSizeChangeForm
          initial={shirtSizeInitial}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    case 'Vest Size Change':
      return (
        <VestSizeChangeForm
          initial={vestSizeInitial}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      );
    default:
      return null;
  }
}

// ---- Shared submit error banner used inside each form ----
function FormErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div
      className="card"
      style={{
        borderColor: 'var(--brand-danger)',
        background: 'rgba(220, 38, 38, 0.04)',
        marginBottom: 12,
      }}
      role="alert"
    >
      <div className="card-title" style={{ marginBottom: 4 }}>
        Submit Failed
      </div>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</p>
    </div>
  );
}

function FormFooter({ saving, onCancel, submitLabel = 'Submit Request' }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 16,
      }}
    >
      <button
        type="button"
        className="btn btn-outline"
        onClick={onCancel}
        disabled={saving}
      >
        Cancel
      </button>
      <button type="submit" className="btn btn-primary" disabled={saving}>
        {saving ? 'Submitting…' : submitLabel}
      </button>
    </div>
  );
}

// ---- Shared shipping-address confirmation -----------------------------
// Every physical-goods form (Vest, Shirt, Business Cards, Badge Card)
// renders this block above its submit button so the user sees exactly
// where the item will ship and explicitly confirms before submitting.
//
// Behavior:
//   - If there IS an address on file, render it read-only with a
//     "Mail to this address" checkbox. The parent form blocks submit
//     until the box is checked.
//   - If there IS NO address on file, render a warning pointing the
//     user back to the "Update Address" button on their profile. The
//     parent form blocks submit in that case too.
//
// We keep the address read-only here on purpose — editing it belongs in
// the Update Address flow, which writes the change to the team record so
// future requests also ship to the right place.
function MailingAddressConfirm({ mailingAddress, checked, onChange, error }) {
  if (!mailingAddress) {
    return (
      <div
        className="card"
        style={{
          borderColor: 'var(--brand-danger)',
          background: 'rgba(220, 38, 38, 0.04)',
          marginBottom: 12,
        }}
        role="alert"
      >
        <div className="card-title" style={{ marginBottom: 4 }}>
          No Mailing Address On File
        </div>
        <p style={{ margin: 0, fontSize: 13 }}>
          We need a mailing address before we can ship this item. Close this
          window and use <strong>Update Address</strong> on your profile to
          add one, then try again.
        </p>
      </div>
    );
  }
  const line2 = [mailingAddress.city, mailingAddress.state, mailingAddress.zip]
    .filter(Boolean)
    .join(', ');
  return (
    <div
      style={{
        border: '1px solid var(--brand-border)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 12,
        background: '#fafafa',
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--brand-muted)',
          marginBottom: 6,
        }}
      >
        Ship To
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.4, marginBottom: 10 }}>
        {mailingAddress.street && <div>{mailingAddress.street}</div>}
        {line2 && <div>{line2}</div>}
        {mailingAddress.country && <div>{mailingAddress.country}</div>}
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span>
          Mail to this address.{' '}
          <span style={{ color: 'var(--brand-danger)' }}>*</span>
        </span>
      </label>
      <p
        style={{
          margin: '6px 0 0 24px',
          color: 'var(--brand-muted)',
          fontSize: 12,
        }}
      >
        If this address is incorrect, close this window and use{' '}
        <strong>Update Address</strong> on your account page to update.
      </p>
      {error && (
        <span className="error" style={{ marginTop: 6, display: 'block' }}>
          {error}
        </span>
      )}
    </div>
  );
}

// ---- Vest + Shirt share almost all fields; only Shirt adds Fit.
// Both forms are size + quantity at their core — we deliberately don't
// carry Notes or Style here because in practice neither got used enough
// to justify the extra clutter on screen.
function VestOrShirtForm({ kind, mailingAddress, saving, error, onSave, onCancel }) {
  const [model, setModel] = useState({
    Quantity: 1,
    Size: '',
    Fit: '',
    // Vest-only: Yellow / Orange / Other — with a free-text field that
    // appears when Other is picked, so requesters can ask for black,
    // hi-vis green, etc. without changing the code.
    VestColorChoice: '',
    VestColorOther: '',
    ConfirmAddress: false,
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.Size) next.Size = 'Pick a size.';
    if (!model.Quantity || Number(model.Quantity) < 1) {
      next.Quantity = 'Quantity must be 1 or more.';
    }
    if (kind === 'Shirt' && !model.Fit) next.Fit = 'Pick a fit.';
    if (kind === 'Vest') {
      if (!model.VestColorChoice) {
        next.VestColor = 'Pick a color.';
      } else if (
        model.VestColorChoice === 'Other' &&
        !model.VestColorOther.trim()
      ) {
        next.VestColor = 'Type the color you want.';
      }
    }
    if (!mailingAddress) {
      next.ConfirmAddress =
        'Add a mailing address on your profile before submitting.';
    } else if (!model.ConfirmAddress) {
      next.ConfirmAddress = 'Confirm the shipping address to continue.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    // Resolve the final VestColor: for Yellow/Orange it's the choice
    // itself; for Other it's whatever the user typed.
    const vestColor =
      kind === 'Vest'
        ? model.VestColorChoice === 'Other'
          ? model.VestColorOther.trim()
          : model.VestColorChoice
        : '';
    onSave({
      Quantity: Number(model.Quantity),
      Size: model.Size,
      Fit: kind === 'Shirt' ? model.Fit : '',
      VestColor: vestColor,
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <div className="form-grid">
        <SelectField
          label="Size *"
          value={model.Size}
          onChange={(v) => set('Size', v)}
          options={APPAREL_SIZES}
        />
        {errors.Size && <span className="error">{errors.Size}</span>}

        <TextField
          label="Quantity *"
          type="number"
          value={model.Quantity}
          onChange={(v) => set('Quantity', v)}
          min={1}
          error={errors.Quantity}
        />

        {kind === 'Shirt' && (
          <>
            <SelectField
              label="Fit *"
              value={model.Fit}
              onChange={(v) => set('Fit', v)}
              options={SHIRT_FITS}
            />
            {errors.Fit && <span className="error">{errors.Fit}</span>}
          </>
        )}

        {kind === 'Vest' && (
          <>
            <SelectField
              label="Vest Color *"
              value={model.VestColorChoice}
              onChange={(v) => set('VestColorChoice', v)}
              options={VEST_COLORS}
            />
            {model.VestColorChoice === 'Other' && (
              <TextField
                label="Other Color *"
                value={model.VestColorOther}
                onChange={(v) => set('VestColorOther', v)}
                placeholder="e.g. Hi-Vis Green"
              />
            )}
            {errors.VestColor && (
              <span className="error">{errors.VestColor}</span>
            )}
          </>
        )}
      </div>
      <MailingAddressConfirm
        mailingAddress={mailingAddress}
        checked={model.ConfirmAddress}
        onChange={(v) => set('ConfirmAddress', v)}
        error={errors.ConfirmAddress}
      />
      <FormFooter saving={saving} onCancel={onCancel} />
    </form>
  );
}

function BusinessCardsForm({ mailingAddress, cardContact, saving, error, onSave, onCancel }) {
  // Every printed line on the card comes straight from the team record —
  // nothing is editable here. The user's job is to read the details and
  // confirm. Fixes to name / title / email go through a Director; phone
  // updates go through Update Phone on the profile card.
  const [model, setModel] = useState({
    ConfirmDetails: false,
    ConfirmAddress: false,
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const cardName = cardContact?.name || '';
  const cardTitle = cardContact?.title || '';
  const cardEmail = cardContact?.email || '';
  const cardPhone = cardContact?.phone || '';

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!cardName.trim()) {
      // Belt-and-suspenders: if the team record has no name on file,
      // there's nothing to print — send them to fix that first.
      next.ConfirmDetails =
        'We don\u2019t have a name on file for your team record. Ask a Director to add one before requesting cards.';
    } else if (!model.ConfirmDetails) {
      next.ConfirmDetails =
        'Confirm the details above are correct to continue.';
    }
    if (!mailingAddress) {
      next.ConfirmAddress =
        'Add a mailing address on your profile before submitting.';
    } else if (!model.ConfirmAddress) {
      next.ConfirmAddress = 'Confirm the shipping address to continue.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      CardNameLine: cardName.trim(),
      CardTitleLine: cardTitle.trim(),
      CardPhoneLine: cardPhone.trim(),
      CardEmailLine: cardEmail.trim(),
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <p style={{ marginTop: 0, color: 'var(--brand-muted)', fontSize: 13 }}>
        These details will be printed exactly as shown. Check them carefully.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <ReadOnlyField label="Name on Card" value={cardName} />
        <ReadOnlyField label="Title on Card" value={cardTitle} />
        <ReadOnlyField label="Email on Card" value={cardEmail} />
        <ReadOnlyField label="Phone on Card" value={formatPhone(cardPhone)} />
      </div>
      <div
        style={{
          border: '1px solid var(--brand-border)',
          borderRadius: 8,
          padding: 12,
          marginTop: 12,
          marginBottom: 12,
          background: '#fafafa',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={!!model.ConfirmDetails}
            onChange={(e) => set('ConfirmDetails', e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>The details above are correct.</span>
        </label>
        <p
          style={{
            margin: '6px 0 0 24px',
            color: 'var(--brand-muted)',
            fontSize: 12,
          }}
        >
          If this phone number is incorrect, close this window and use{' '}
          <strong>Update Phone</strong> on your account page to change it.
          If your name, title, or email are incorrect, please notify your
          manager.
        </p>
        {errors.ConfirmDetails && (
          <span className="error" style={{ marginTop: 6, display: 'block' }}>
            {errors.ConfirmDetails}
          </span>
        )}
      </div>
      <MailingAddressConfirm
        mailingAddress={mailingAddress}
        checked={model.ConfirmAddress}
        onChange={(v) => set('ConfirmAddress', v)}
        error={errors.ConfirmAddress}
      />
      <FormFooter saving={saving} onCancel={onCancel} />
    </form>
  );
}

// Simple read-only label + value display, used by the Business Cards form
// for the printed-contact fields (name, email, phone). Matches the visual
// weight of the FormField inputs so the form reads consistently.
function ReadOnlyField({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--brand-muted)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.4 }}>
        {value || (
          <span style={{ color: 'var(--brand-muted)' }}>(not on file)</span>
        )}
      </div>
    </div>
  );
}

function BadgeCardForm({ mailingAddress, cardContact, saving, error, onSave, onCancel }) {
  // Badge cards reuse the Business Cards pattern exactly — the printed
  // info is the same (name, title, email, phone, QR) so we pull it from
  // the team record and let the user confirm instead of re-typing. Only
  // the saved columns differ (Badge* vs Card*).
  const [model, setModel] = useState({
    ConfirmDetails: false,
    ConfirmAddress: false,
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const cardName = cardContact?.name || '';
  const cardTitle = cardContact?.title || '';
  const cardEmail = cardContact?.email || '';
  const cardPhone = cardContact?.phone || '';

  // Split the full name into first + last for the BadgeFirstName /
  // BadgeLastName columns. Everything after the first space goes into
  // last name (handles "Mary Ann Smith" → "Mary" / "Ann Smith").
  const [firstName = '', ...restName] = cardName.trim().split(/\s+/);
  const lastName = restName.join(' ');

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!cardName.trim()) {
      next.ConfirmDetails =
        'We don\u2019t have a name on file for your team record. Ask a Director to add one before requesting a badge.';
    } else if (!model.ConfirmDetails) {
      next.ConfirmDetails =
        'Confirm the details above are correct to continue.';
    }
    if (!mailingAddress) {
      next.ConfirmAddress =
        'Add a mailing address on your profile before submitting.';
    } else if (!model.ConfirmAddress) {
      next.ConfirmAddress = 'Confirm the shipping address to continue.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      BadgeFirstName: firstName,
      BadgeLastName: lastName,
      BadgeJobTitle: cardTitle.trim(),
      // Phone + email aren't stored on Badge-specific columns, but are
      // still captured in the notification email via CardPhoneLine /
      // CardEmailLine so Directors see exactly what'll be printed.
      CardPhoneLine: cardPhone.trim(),
      CardEmailLine: cardEmail.trim(),
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <p style={{ marginTop: 0, color: 'var(--brand-muted)', fontSize: 13 }}>
        These details will be printed exactly as shown. Check them carefully.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <ReadOnlyField label="Name on Badge" value={cardName} />
        <ReadOnlyField label="Title on Badge" value={cardTitle} />
        <ReadOnlyField label="Email on Badge" value={cardEmail} />
        <ReadOnlyField label="Phone on Badge" value={formatPhone(cardPhone)} />
      </div>
      <div
        style={{
          border: '1px solid var(--brand-border)',
          borderRadius: 8,
          padding: 12,
          marginTop: 12,
          marginBottom: 12,
          background: '#fafafa',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={!!model.ConfirmDetails}
            onChange={(e) => set('ConfirmDetails', e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>The details above are correct.</span>
        </label>
        <p
          style={{
            margin: '6px 0 0 24px',
            color: 'var(--brand-muted)',
            fontSize: 12,
          }}
        >
          If this phone number is incorrect, close this window and use{' '}
          <strong>Update Phone</strong> on your account page to change it.
          If your name, title, or email are incorrect, please notify your
          manager.
        </p>
        {errors.ConfirmDetails && (
          <span className="error" style={{ marginTop: 6, display: 'block' }}>
            {errors.ConfirmDetails}
          </span>
        )}
      </div>
      <MailingAddressConfirm
        mailingAddress={mailingAddress}
        checked={model.ConfirmAddress}
        onChange={(v) => set('ConfirmAddress', v)}
        error={errors.ConfirmAddress}
      />
      <FormFooter saving={saving} onCancel={onCancel} />
    </form>
  );
}

function AddressChangeForm({ initial, saving, error, onSave, onCancel }) {
  // Prefill with the user's address on file (passed in from MyAccount).
  // Most updates are one-field tweaks — a ZIP correction, a new apartment
  // number — so starting with the existing values is friendlier than a
  // blank form.
  const [model, setModel] = useState({
    NewStreet: initial?.NewStreet || '',
    NewCity: initial?.NewCity || '',
    NewState: initial?.NewState || '',
    NewZip: initial?.NewZip || '',
    NewCountry: initial?.NewCountry || 'United States',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.NewStreet.trim()) next.NewStreet = 'Street is required.';
    if (!model.NewCity.trim()) next.NewCity = 'City is required.';
    if (!model.NewState.trim()) next.NewState = 'State / Province is required.';
    if (!model.NewZip.trim()) next.NewZip = 'Zip / Postal Code is required.';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      NewStreet: model.NewStreet.trim(),
      NewCity: model.NewCity.trim(),
      NewState: model.NewState.trim(),
      NewZip: model.NewZip.trim(),
      NewCountry: model.NewCountry.trim(),
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <div className="form-grid">
        <TextField
          label="Street *"
          value={model.NewStreet}
          onChange={(v) => set('NewStreet', v)}
          error={errors.NewStreet}
          full
          required
        />
        <TextField
          label="City *"
          value={model.NewCity}
          onChange={(v) => set('NewCity', v)}
          error={errors.NewCity}
          required
        />
        <TextField
          label="State / Province *"
          value={model.NewState}
          onChange={(v) => set('NewState', v)}
          error={errors.NewState}
          required
        />
        <TextField
          label="Zip / Postal Code *"
          value={model.NewZip}
          onChange={(v) => set('NewZip', v)}
          error={errors.NewZip}
          required
        />
        <TextField
          label="Country"
          value={model.NewCountry}
          onChange={(v) => set('NewCountry', v)}
        />
      </div>
      <FormFooter saving={saving} onCancel={onCancel} submitLabel="Submit Update" />
    </form>
  );
}

function PhoneChangeForm({ initial, saving, error, onSave, onCancel }) {
  // Prefill with the current number on file so the user can edit digits
  // in place instead of re-typing the whole thing. Free-text so any
  // formatting the user prefers (dashes, parens, +1 prefix) passes
  // through unchanged — the Director reviewing the request sees exactly
  // what the user typed.
  const [model, setModel] = useState({
    NewPhone: initial?.NewPhone || '',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.NewPhone.trim()) next.NewPhone = 'Phone number is required.';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      NewPhone: model.NewPhone.trim(),
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <p style={{ marginTop: 0, color: 'var(--brand-muted)', fontSize: 13 }}>
        Include country code if it's outside the U.S.
      </p>
      <div className="form-grid">
        <PhoneField
          label="Phone Number *"
          value={model.NewPhone}
          onChange={(v) => set('NewPhone', v)}
          error={errors.NewPhone}
          full
          required
        />
      </div>
      <FormFooter saving={saving} onCancel={onCancel} submitLabel="Submit Update" />
    </form>
  );
}

function EmergencyContactChangeForm({ initial, saving, error, onSave, onCancel }) {
  // Prefill with the contact on file so the user can correct a single
  // field (new phone number, different relationship) without re-typing
  // everything. Same auto-apply pattern as Address / Phone changes: the
  // values write back to the user's Proxemics Team record immediately
  // and a Fulfilled request row is logged for audit.
  //
  // Name + Phone are required; Relation is optional because it's a
  // nice-to-have, not a must-have for reaching the contact in a real
  // emergency.
  const [model, setModel] = useState({
    NewEmergencyContactName: initial?.NewEmergencyContactName || '',
    NewEmergencyContactPhone: initial?.NewEmergencyContactPhone || '',
    NewEmergencyContactRelation: initial?.NewEmergencyContactRelation || '',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.NewEmergencyContactName.trim()) {
      next.NewEmergencyContactName = 'Name is required.';
    }
    if (!model.NewEmergencyContactPhone.trim()) {
      next.NewEmergencyContactPhone = 'Phone number is required.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      NewEmergencyContactName: model.NewEmergencyContactName.trim(),
      NewEmergencyContactPhone: model.NewEmergencyContactPhone.trim(),
      NewEmergencyContactRelation: model.NewEmergencyContactRelation.trim(),
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <p style={{ marginTop: 0, color: 'var(--brand-muted)', fontSize: 13 }}>
        Include country code if it's outside the U.S.
      </p>
      <div className="form-grid">
        <TextField
          label="Name *"
          value={model.NewEmergencyContactName}
          onChange={(v) => set('NewEmergencyContactName', v)}
          error={errors.NewEmergencyContactName}
          full
          required
        />
        <PhoneField
          label="Phone Number *"
          value={model.NewEmergencyContactPhone}
          onChange={(v) => set('NewEmergencyContactPhone', v)}
          error={errors.NewEmergencyContactPhone}
          required
        />
        <RelationField
          value={model.NewEmergencyContactRelation}
          onChange={(v) => set('NewEmergencyContactRelation', v)}
        />
      </div>
      <FormFooter saving={saving} onCancel={onCancel} submitLabel="Submit Update" />
    </form>
  );
}

function ShirtSizeChangeForm({ initial, saving, error, onSave, onCancel }) {
  // Same auto-apply pattern as Address / Phone / Emergency Contact —
  // saving writes the new size + fit back to the team record
  // immediately and logs a Fulfilled request row for audit. Prefilled
  // with the values on file so the user just picks new ones from the
  // dropdowns.
  const [model, setModel] = useState({
    NewShirtSize: initial?.NewShirtSize || '',
    NewShirtFit: initial?.NewShirtFit || '',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.NewShirtSize) next.NewShirtSize = 'Pick a size.';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      NewShirtSize: model.NewShirtSize,
      NewShirtFit: model.NewShirtFit,
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <div className="form-grid">
        <SelectField
          label="Shirt Fit"
          value={model.NewShirtFit}
          onChange={(v) => set('NewShirtFit', v)}
          options={SHIRT_FITS}
        />
        <SelectField
          label="Shirt Size *"
          value={model.NewShirtSize}
          onChange={(v) => set('NewShirtSize', v)}
          options={APPAREL_SIZES}
          required
        />
        {errors.NewShirtSize && (
          <span className="error">{errors.NewShirtSize}</span>
        )}
      </div>
      <FormFooter saving={saving} onCancel={onCancel} submitLabel="Submit Update" />
    </form>
  );
}

function VestSizeChangeForm({ initial, saving, error, onSave, onCancel }) {
  // Parallels ShirtSizeChangeForm. Saving writes VestSize + VestColor
  // (and VestColorOther when "Other" is chosen) back to the team record
  // immediately and files a Fulfilled request row for audit. Prefilled
  // with the values on file so the user only picks what they're changing.
  const [model, setModel] = useState({
    NewVestSize: initial?.NewVestSize || '',
    NewVestColor: initial?.NewVestColor || '',
    NewVestColorOther: initial?.NewVestColorOther || '',
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.NewVestSize) next.NewVestSize = 'Pick a size.';
    if (!model.NewVestColor) next.NewVestColor = 'Pick a color.';
    if (model.NewVestColor === 'Other' && !model.NewVestColorOther.trim()) {
      next.NewVestColorOther = 'Enter the color.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      NewVestSize: model.NewVestSize,
      NewVestColor: model.NewVestColor,
      // Clear the fill-in when the user picked a preset so no stale
      // free-text leaks into the record.
      NewVestColorOther:
        model.NewVestColor === 'Other' ? model.NewVestColorOther.trim() : '',
    });
  };

  return (
    <form onSubmit={submit}>
      <FormErrorBanner error={error} />
      <div className="form-grid">
        <SelectField
          label="Safety Vest Size *"
          value={model.NewVestSize}
          onChange={(v) => set('NewVestSize', v)}
          options={APPAREL_SIZES}
          required
        />
        {errors.NewVestSize && (
          <span className="error">{errors.NewVestSize}</span>
        )}
        <SelectField
          label="Safety Vest Color *"
          value={model.NewVestColor}
          onChange={(v) => {
            set('NewVestColor', v);
            // Switching off "Other" clears the fill-in so we don't keep
            // a stale color string the user can't see.
            if (v !== 'Other') set('NewVestColorOther', '');
          }}
          options={VEST_COLORS}
          required
        />
        {errors.NewVestColor && (
          <span className="error">{errors.NewVestColor}</span>
        )}
        {model.NewVestColor === 'Other' && (
          <>
            <TextField
              label="Other Color *"
              value={model.NewVestColorOther}
              onChange={(v) => set('NewVestColorOther', v)}
              placeholder="e.g. Hi-Vis Green"
            />
            {errors.NewVestColorOther && (
              <span className="error">{errors.NewVestColorOther}</span>
            )}
          </>
        )}
      </div>
      <FormFooter saving={saving} onCancel={onCancel} submitLabel="Submit Update" />
    </form>
  );
}

// =====================================================================
// Pre-Launch Cleanup card
//
// Temporary admin tool: wipes Coverage Activity events the current
// user posted and timesheets they submitted, so the portal launches
// without setup-era noise in those audit feeds. Identity is matched
// on PostedBy / SubmitterEmail / SubmitterName (case-insensitive,
// trimmed) so legacy rows written before email was captured still
// match by display name.
//
// Both deletes are gated behind a window.confirm() with the count
// included so an accidental click is recoverable up to that point.
// Failures during the per-row delete loop are tallied — partial
// success surfaces in the result banner. Safe to remove this entire
// component plus its imports + the gated <section> in render() after
// go-live; no other code references it.
// =====================================================================

function PreLaunchCleanupCard({ authName, authEmail }) {
  // null = still loading the initial counts; numbers = loaded.
  const [coverageEvents, setCoverageEvents] = useState(null);
  const [mySheets, setMySheets] = useState(null);
  const [myInvoices, setMyInvoices] = useState(null);
  // Every Billing Tracker dated before the go-live week — across ALL
  // submitters, not just the current user. Used by the one-time
  // "clear pre–go-live trackers" purge so nobody can carry forward a
  // week entered while testing before the Aug 3, 2026 launch.
  const [preGoLiveSheets, setPreGoLiveSheets] = useState(null);
  // Migration orphans: trackers on/after go-live whose WeekStart is NOT a
  // Sunday. When the week ran Mon→Sun these were anchored to a Monday;
  // after the switch to Sun→Sat (Aug 2026) they no longer line up with the
  // grid and render mis-aligned. Deleting them lets the owner re-enter the
  // week cleanly on the correct Sunday-start grid. Cross-user, like the
  // pre–go-live row below.
  const [misalignedSheets, setMisalignedSheets] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Case-insensitive match. Strips whitespace so an email captured
  // with a trailing space (or a different casing) still resolves.
  const norm = (v) => String(v || '').trim().toLowerCase();
  const matchesMe = useCallback(
    (...candidates) => {
      const me = new Set(
        [authName, authEmail].map(norm).filter(Boolean),
      );
      return candidates.some((c) => c && me.has(norm(c)));
    },
    [authName, authEmail],
  );

  const refresh = useCallback(async () => {
    setError('');
    try {
      const [events, sheets, invoices] = await Promise.all([
        listSalesTrackerEvents(),
        listTimesheets(),
        listInvoices(),
      ]);
      setCoverageEvents(
        events.filter((e) => matchesMe(e.PostedBy)),
      );
      setMySheets(
        sheets.filter((t) =>
          matchesMe(t.SubmitterEmail, t.SubmitterName, t.PostedBy),
        ),
      );
      // Pre–go-live purge candidates: any tracker whose week starts
      // before BILLING_TRACKER_FIRST_WEEK, regardless of who submitted
      // it. WeekStart is a 'YYYY-MM-DD' string so a plain string compare
      // is a correct date compare here.
      setPreGoLiveSheets(
        sheets.filter(
          (t) => (t.WeekStart || '') && (t.WeekStart || '') < BILLING_TRACKER_FIRST_WEEK,
        ),
      );
      // Mis-aligned (non-Sunday-start) trackers on/after go-live. Parse the
      // YYYY-MM-DD parts locally (no UTC shift) and flag any whose weekday
      // isn't Sunday (getDay() !== 0). Only looks at go-live-era weeks so it
      // doesn't overlap the pre–go-live purge above.
      const startsOnSunday = (ws) => {
        const m = String(ws || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) return true; // no/invalid date → don't flag it
        return new Date(
          Number(m[1]),
          Number(m[2]) - 1,
          Number(m[3]),
        ).getDay() === 0;
      };
      setMisalignedSheets(
        sheets.filter((t) => {
          const ws = t.WeekStart || '';
          return ws && ws >= BILLING_TRACKER_FIRST_WEEK && !startsOnSunday(ws);
        }),
      );
      // Invoices have a CreatedBy field set at generation time (the
      // Director's name or email). Same matchesMe rule as the other
      // two lists — strip + lowercase before comparing.
      setMyInvoices(
        invoices.filter((inv) => matchesMe(inv.CreatedBy)),
      );
    } catch (err) {
      setError(err?.message || String(err));
    }
  }, [matchesMe]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Generic per-row delete loop. Tallies success / failure so the
  // banner can call out partial results. Sequential rather than
  // Promise.all so we don't blow up SharePoint's throttle limits on
  // a fresh tenant — these lists are small, the loop is fast enough.
  const wipe = async (items, deleteFn, label) => {
    if (!items || !items.length) return;
    const ok = window.confirm(
      `Permanently delete ${items.length} ${label}? ` +
        `This can't be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    setMessage('');
    setError('');
    let success = 0;
    let failures = 0;
    for (const item of items) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteFn(item.id);
        success += 1;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[cleanup] delete failed', item.id, err);
        failures += 1;
      }
    }
    setBusy(false);
    setMessage(
      failures === 0
        ? `Deleted ${success} ${label}.`
        : `Deleted ${success} ${label}; ${failures} could not be removed (check console).`,
    );
    await refresh();
  };

  const coverageCount = coverageEvents == null ? null : coverageEvents.length;
  const sheetCount = mySheets == null ? null : mySheets.length;
  const invoiceCount = myInvoices == null ? null : myInvoices.length;
  const preGoLiveCount = preGoLiveSheets == null ? null : preGoLiveSheets.length;
  const misalignedCount =
    misalignedSheets == null ? null : misalignedSheets.length;

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 0',
    borderTop: '1px solid var(--brand-border, #e5e7eb)',
  };

  return (
    <div
      className="card"
      style={{ borderColor: 'var(--brand-danger, #c53030)' }}
    >
      <div className="card-title">Wipe My Test Data</div>
      <p style={{ margin: 0, color: 'var(--brand-muted)', fontSize: 13 }}>
        Removes Coverage Activity events you posted and timesheets you
        submitted so the portal launches with a clean slate. Matches on
        your name <em>or</em> email so legacy rows still resolve. The last
        two rows are different — they act across <em>every</em> user:{' '}
        <strong>Pre–go-live Billing Trackers</strong> clears any tracker
        dated before go-live, and <strong>Mis-aligned Billing Trackers</strong>{' '}
        clears the Monday-anchored leftovers from before the Sunday–Saturday
        switch (so their owners can re-enter the week cleanly). Cannot be undone.
      </p>
      {error && (
        <div className="error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
      {message && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            background: 'var(--brand-primary-soft, #fff4e6)',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {message}
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <div style={rowStyle}>
          <span>
            <strong>Coverage Activity:</strong>{' '}
            {coverageCount == null
              ? 'counting…'
              : `${coverageCount} event${coverageCount === 1 ? '' : 's'} posted by you`}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy || !coverageCount}
            onClick={() =>
              wipe(coverageEvents, deleteSalesTrackerEvent, 'Coverage Activity events')
            }
          >
            {busy ? 'Working…' : 'Delete all'}
          </button>
        </div>
        <div style={rowStyle}>
          <span>
            <strong>Billing Tracker:</strong>{' '}
            {sheetCount == null
              ? 'counting…'
              : `${sheetCount} entr${sheetCount === 1 ? 'y' : 'ies'} submitted by you`}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy || !sheetCount}
            onClick={() => wipe(mySheets, deleteTimesheet, 'timesheets')}
          >
            {busy ? 'Working…' : 'Delete all'}
          </button>
        </div>
        <div style={rowStyle}>
          <span>
            <strong>Invoices:</strong>{' '}
            {invoiceCount == null
              ? 'counting…'
              : `${invoiceCount} invoice${invoiceCount === 1 ? '' : 's'} generated by you`}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy || !invoiceCount}
            onClick={() => wipe(myInvoices, deleteInvoice, 'invoices')}
          >
            {busy ? 'Working…' : 'Delete all'}
          </button>
        </div>
        {/* Cross-user purge: EVERY Billing Tracker dated before the
            Aug 3, 2026 go-live week, no matter who submitted it. This is
            the "you can't go back in time" cleanup — it clears the test
            weeks people entered against the old June 22 floor. Deliberately
            spans all submitters (unlike the rows above, which are scoped
            to the current user), so its copy calls that out. */}
        <div style={{ ...rowStyle, borderTop: '2px solid var(--brand-danger, #c53030)' }}>
          <span>
            <strong>Pre–go-live Billing Trackers:</strong>{' '}
            {preGoLiveCount == null
              ? 'counting…'
              : `${preGoLiveCount} entr${preGoLiveCount === 1 ? 'y' : 'ies'} before Aug 3 (all users)`}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy || !preGoLiveCount}
            onClick={() =>
              wipe(preGoLiveSheets, deleteTimesheet, 'pre–go-live Billing Trackers')
            }
          >
            {busy ? 'Working…' : 'Delete all'}
          </button>
        </div>
        {/* Cross-user purge: every go-live-era Billing Tracker whose week
            does NOT start on a Sunday — i.e. the Monday-anchored leftovers
            from before the Sun→Sat switch. They render mis-aligned and
            can't be re-anchored in place, so this clears them for a clean
            re-entry. Spans all submitters. */}
        <div style={{ ...rowStyle, borderTop: '2px solid var(--brand-danger, #c53030)' }}>
          <span>
            <strong>Mis-aligned Billing Trackers:</strong>{' '}
            {misalignedCount == null
              ? 'counting…'
              : `${misalignedCount} entr${misalignedCount === 1 ? 'y' : 'ies'} not on a Sunday start (all users)`}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={busy || !misalignedCount}
            onClick={() =>
              wipe(misalignedSheets, deleteTimesheet, 'mis-aligned Billing Trackers')
            }
          >
            {busy ? 'Working…' : 'Delete all'}
          </button>
        </div>
      </div>
      <p
        style={{
          marginTop: 12,
          marginBottom: 0,
          color: 'var(--brand-muted)',
          fontSize: 12,
        }}
      >
        Refresh the page after deleting to verify the counts went to zero.
      </p>
    </div>
  );
}
